import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Filter, MapPin, Home as HomeIcon, ArrowUpDown, ChevronDown, Bed, Maximize, Sparkles, Loader2, Heart, ChevronLeft, ChevronRight, Share2, Link2, MessageCircle, Check } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import { trackEvent } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}/${url.replace(/^\/+/, '')}`;
};

const ImageCarousel = ({ images, title, listingId }) => {
  const [idx, setIdx] = useState(0);
  const touchStartX = React.useRef(null);
  if (!images || images.length <= 1) {
    return <img src={resolveImg(images?.[0])} alt={title} className="w-full h-full object-cover" loading="lazy" decoding="async" />;
  }
  const prev = (e) => { if (e) e.stopPropagation(); setIdx((i) => (i === 0 ? images.length - 1 : i - 1)); };
  const next = (e) => { if (e) e.stopPropagation(); setIdx((i) => (i === images.length - 1 ? 0 : i + 1)); };
  return (
    <div className="w-full h-full"
      onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
      onTouchEnd={e => {
        if (touchStartX.current === null) return;
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) { diff > 0 ? next() : prev(); }
        touchStartX.current = null;
      }}
    >
      <img src={resolveImg(images[idx])} alt={`${title} ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" decoding="async" />
      <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity z-10" data-testid={`carousel-prev-${listingId}`}>
        <ChevronLeft className="w-4 h-4 text-slate-700" />
      </button>
      <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity z-10" data-testid={`carousel-next-${listingId}`}>
        <ChevronRight className="w-4 h-4 text-slate-700" />
      </button>
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
        {images.map((_, i) => (
          <span key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${i === idx ? 'bg-white w-3' : 'bg-white/50'}`} />
        ))}
      </div>
    </div>
  );
};

const TYPE_TO_PATH = {
  buy: '/properties-for-sale',
  rent: '/properties-for-rent',
  new_projects: '/new-projects',
};

const BrowseListings = ({ defaultType }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalLoaded, setTotalLoaded] = useState(0);
  const itemsPerPage = 4;
  
  // User and saved properties state
  const [user, setUser] = useState(null);
  const [savedProperties, setSavedProperties] = useState([]);
  const [savingProperty, setSavingProperty] = useState(null);
  const [shareOpenId, setShareOpenId] = useState(null);
  const [linkCopiedId, setLinkCopiedId] = useState(null);

  const handleShareCopyLink = (e, listingId) => {
    e.stopPropagation();
    const url = `${window.location.origin}/property/${listingId}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopiedId(listingId);
      setTimeout(() => setLinkCopiedId(null), 2000);
    }).catch(() => {
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setLinkCopiedId(listingId);
      setTimeout(() => setLinkCopiedId(null), 2000);
    });
    setShareOpenId(null);
  };

  const handleShareWhatsApp = (e, listing) => {
    e.stopPropagation();
    const url = `${window.location.origin}/property/${listing.slug || listing.id}`;
    const text = encodeURIComponent(`Check out this property on PropTick:\n\n*${listing.title}*\n${listing.locality}, ${listing.city}\n\n${url}`);
    const a = document.createElement('a');
    a.href = `https://wa.me/?text=${text}`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
    setShareOpenId(null);
  };
  
  // Initialize listing type from route prop, URL params, or default to 'buy'
  const initialListingType = defaultType || searchParams.get('listing_type') || 'buy';
  const initialNewProjects = searchParams.get('new_projects') === 'true';
  const [listingType, setListingType] = useState(initialNewProjects ? 'new_projects' : initialListingType);
  
  // Initialize city from URL params
  const initialCity = searchParams.get('city') || '';
  
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    city: initialCity,
    locality: '',
    property_type: searchParams.get('property_type') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    bedrooms: searchParams.get('bedrooms') || '',
    min_sqft: '',
    max_sqft: '',
    verified_only: false,
    property_status: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [rentPeriodFilter, setRentPeriodFilter] = useState('all');
  const [propertyStatusFilter, setPropertyStatusFilter] = useState('all');
  const [aiSearchMode, setAiSearchMode] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiSearching, setAiSearching] = useState(false);
  const [aiExplanation, setAiExplanation] = useState('');
  
  // Show filters panel if city is pre-selected
  const [selectedCity, setSelectedCity] = useState(initialCity);
  const [availableCities, setAvailableCities] = useState([]);

  // Fetch cities with properties (with retry)
  useEffect(() => {
    let retries = 0;
    const fetchCities = () => {
      fetch(`${API}/listings/city-counts`)
        .then(r => {
          if (!r.ok) throw new Error('Not OK');
          return r.json();
        })
        .then(data => {
          const cities = Object.keys(data).filter(c => c).sort();
          if (cities.length > 0) {
            setAvailableCities(cities);
          } else if (retries < 2) {
            retries++;
            setTimeout(fetchCities, 1000 * retries);
          }
        })
        .catch(() => {
          if (retries < 2) {
            retries++;
            setTimeout(fetchCities, 1000 * retries);
          }
        });
    };
    fetchCities();
  }, []);

  // Load user and saved properties
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (storedUser && token) {
      const userData = JSON.parse(storedUser);
      if (userData.role === 'user') {
        setUser(userData);
        fetchSavedProperties(token);
      }
    }
  }, []);

  const fetchSavedProperties = async (token) => {
    try {
      const response = await axios.get(`${API}/user/saved-properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedProperties(response.data.map(p => p.id));
    } catch (error) {
      console.error('Error fetching saved properties:', error);
    }
  };

  const handleSaveProperty = async (e, listingId) => {
    e.stopPropagation(); // Prevent card click
    
    if (!user) {
      navigate('/login');
      return;
    }
    
    const token = localStorage.getItem('token');
    const isSaved = savedProperties.includes(listingId);
    
    setSavingProperty(listingId);
    
    try {
      if (isSaved) {
        await axios.delete(`${API}/user/save-property/${listingId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSavedProperties(prev => prev.filter(id => id !== listingId));
      } else {
        await axios.post(`${API}/user/save-property`, 
          { listing_id: listingId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSavedProperties(prev => [...prev, listingId]);
      }
    } catch (error) {
      console.error('Error saving property:', error);
    } finally {
      setSavingProperty(null);
    }
  };

  const sortOptions = [
    { value: '', label: 'Default' },
    { value: 'newest_first', label: 'Newest First' },
    { value: 'price_low_to_high', label: 'Price: Low to High' },
    { value: 'price_high_to_low', label: 'Price: High to Low' }
  ];

  useEffect(() => {
    fetchListings(true);
  }, [listingType, rentPeriodFilter, propertyStatusFilter]);

  const fetchListings = async (reset = false, retryCount = 0) => {
    try {
      setLoading(true);
      const page = reset ? 1 : currentPage;
      const skip = (page - 1) * itemsPerPage;
      
      const params = new URLSearchParams();
      if (listingType === 'new_projects') {
        params.append('listing_type', 'buy');
        params.append('new_projects', 'true');
      } else {
        params.append('listing_type', listingType);
      }
      if (filters.search) params.append('search', filters.search);
      if (filters.city) params.append('city', filters.city);
      if (filters.locality) params.append('locality', filters.locality);
      if (filters.property_type) params.append('property_type', filters.property_type);
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);
      if (filters.bedrooms) params.append('bedrooms', filters.bedrooms);
      if (filters.min_sqft) params.append('min_sqft', filters.min_sqft);
      if (filters.max_sqft) params.append('max_sqft', filters.max_sqft);
      if (filters.verified_only) params.append('verified_only', 'true');
      if (filters.property_status) params.append('property_status', filters.property_status);
      if (propertyStatusFilter !== 'all' && listingType === 'buy') params.append('property_status', propertyStatusFilter);
      if (rentPeriodFilter !== 'all' && listingType === 'rent') params.append('rent_period', rentPeriodFilter);
      if (sortBy) params.append('sort_by', sortBy);
      params.append('skip', skip);
      params.append('limit', itemsPerPage);

      const response = await axios.get(`${API}/listings/public?${params}`, { timeout: 10000 });
      
      if (reset) {
        setListings(response.data);
        setCurrentPage(1);
        setTotalLoaded(response.data.length);
      } else {
        setListings(response.data);
        setTotalLoaded(response.data.length);
      }
      
      setHasMore(response.data.length === itemsPerPage);
      // Track impressions
      response.data.forEach(l => trackEvent('listing_impression', l.id));
    } catch (error) {
      console.error('Error fetching listings:', error);
      if (retryCount < 2) {
        setTimeout(() => fetchListings(reset, retryCount + 1), 1000 * (retryCount + 1));
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    try {
      const skip = currentPage * itemsPerPage;
      
      const params = new URLSearchParams();
      if (listingType === 'new_projects') {
        params.append('listing_type', 'buy');
        params.append('new_projects', 'true');
      } else {
        params.append('listing_type', listingType);
      }
      if (filters.search) params.append('search', filters.search);
      if (filters.city) params.append('city', filters.city);
      if (filters.locality) params.append('locality', filters.locality);
      if (filters.property_type) params.append('property_type', filters.property_type);
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);
      if (filters.bedrooms) params.append('bedrooms', filters.bedrooms);
      if (filters.min_sqft) params.append('min_sqft', filters.min_sqft);
      if (filters.max_sqft) params.append('max_sqft', filters.max_sqft);
      if (filters.verified_only) params.append('verified_only', 'true');
      if (filters.property_status) params.append('property_status', filters.property_status);
      if (propertyStatusFilter !== 'all' && listingType === 'buy') params.append('property_status', propertyStatusFilter);
      if (rentPeriodFilter !== 'all' && listingType === 'rent') params.append('rent_period', rentPeriodFilter);
      if (sortBy) params.append('sort_by', sortBy);
      params.append('skip', skip);
      params.append('limit', itemsPerPage);

      const response = await axios.get(`${API}/listings/public?${params}`);
      
      setListings([...listings, ...response.data]);
      setCurrentPage(currentPage + 1);
      setTotalLoaded(totalLoaded + response.data.length);
      setHasMore(response.data.length === itemsPerPage);
    } catch (error) {
      console.error('Error loading more listings:', error);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const applyFilters = () => {
    setSelectedCity(filters.city);
    fetchListings(true);
  };

  const clearCityFilter = () => {
    setFilters({ ...filters, city: '' });
    setSelectedCity('');
    // Update URL to remove city param
    navigate('/properties');
    setTimeout(() => fetchListings(true), 100);
  };

  const handleSort = (value) => {
    setSortBy(value);
    setShowSortDropdown(false);
  };

  // Re-fetch when sortBy changes
  useEffect(() => {
    fetchListings(true);
  }, [sortBy]);

  const handleAISearch = async () => {
    if (!aiQuery.trim()) return;
    
    try {
      setAiSearching(true);
      setAiExplanation('');
      
      const response = await axios.post(`${API}/ai-search`, { query: aiQuery });
      const { parsed_filters, explanation } = response.data;
      
      // Map AI filters to our filter state
      const newFilters = {
        search: parsed_filters.search || '',
        city: parsed_filters.city || '',
        locality: parsed_filters.locality || '',
        property_type: parsed_filters.property_type || '',
        min_price: parsed_filters.min_price ? String(parsed_filters.min_price) : '',
        max_price: parsed_filters.max_price ? String(parsed_filters.max_price) : '',
        bedrooms: parsed_filters.bedrooms ? String(parsed_filters.bedrooms) : '',
        min_sqft: parsed_filters.min_sqft ? String(parsed_filters.min_sqft) : '',
        max_sqft: parsed_filters.max_sqft ? String(parsed_filters.max_sqft) : '',
        property_status: parsed_filters.property_status || ''
      };
      
      // Update state for UI
      setFilters(newFilters);
      setAiExplanation(explanation);
      
      // Determine listing type
      const searchListingType = parsed_filters.listing_type || listingType;
      if (parsed_filters.listing_type) {
        setListingType(parsed_filters.listing_type);
      }
      
      // Directly fetch with new filters (don't rely on state update)
      const params = new URLSearchParams();
      if (searchListingType === 'new_projects') {
        params.append('listing_type', 'buy');
        params.append('new_projects', 'true');
      } else {
        params.append('listing_type', searchListingType);
      }
      if (newFilters.search) params.append('search', newFilters.search);
      if (newFilters.city) params.append('city', newFilters.city);
      if (newFilters.locality) params.append('locality', newFilters.locality);
      if (newFilters.property_type) params.append('property_type', newFilters.property_type);
      if (newFilters.min_price) params.append('min_price', newFilters.min_price);
      if (newFilters.max_price) params.append('max_price', newFilters.max_price);
      if (newFilters.bedrooms) params.append('bedrooms', newFilters.bedrooms);
      if (newFilters.min_sqft) params.append('min_sqft', newFilters.min_sqft);
      if (newFilters.max_sqft) params.append('max_sqft', newFilters.max_sqft);
      if (newFilters.property_status) params.append('property_status', newFilters.property_status);
      if (sortBy) params.append('sort_by', sortBy);
      params.append('skip', '0');
      params.append('limit', String(itemsPerPage));

      const listingsResponse = await axios.get(`${API}/listings/public?${params}`);
      setListings(listingsResponse.data);
      setCurrentPage(1);
      setTotalLoaded(listingsResponse.data.length);
      setHasMore(listingsResponse.data.length === itemsPerPage);
      
    } catch (error) {
      console.error('AI search error:', error);
      setAiExplanation('Sorry, I couldn\'t understand that query. Try something like "3BHK apartment under 50 lakhs in Mumbai"');
    } finally {
      setAiSearching(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      city: '',
      locality: '',
      property_type: '',
      min_price: '',
      max_price: '',
      bedrooms: '',
      min_sqft: '',
      max_sqft: '',
      property_status: ''
    });
    setSortBy('');
    setTimeout(() => fetchListings(true), 100);
  };

  const formatPrice = (price) => {
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
    return `₹${price.toLocaleString('en-IN')}`;
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-16">
      <SEO
        title={`${listingType === 'buy' ? 'Buy' : listingType === 'rent' ? 'Rent' : 'New Projects'} Properties in Kerala`}
        description={`Browse verified ${listingType === 'buy' ? 'properties for sale' : listingType === 'rent' ? 'rental properties' : 'new project launches'} in Kerala. Apartments, villas, lands & commercial spaces.`}
        path="/properties"
      />
      <Navbar />

      <div className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Buy/Rent/New Projects Toggle */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-white border border-slate-200 rounded-full p-1 shadow-sm" data-testid="listing-type-toggle">
              <button
                type="button"
                onClick={() => navigate('/properties-for-sale')}
                className={`px-6 py-3 rounded-full font-medium transition-all ${
                  listingType === 'buy'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid="browse-toggle-buy"
              >
                Buy
              </button>
              <button
                type="button"
                onClick={() => navigate('/properties-for-rent')}
                className={`px-6 py-3 rounded-full font-medium transition-all ${
                  listingType === 'rent'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid="browse-toggle-rent"
              >
                Rent
              </button>
              <button
                type="button"
                onClick={() => navigate('/new-projects')}
                className={`px-6 py-3 rounded-full font-medium transition-all ${
                  listingType === 'new_projects'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid="browse-toggle-new-projects"
              >
                New Projects
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight mb-1 sm:mb-2">
                {listingType === 'new_projects' ? 'New Projects' : listingType === 'buy' ? 'Properties for Sale' : 'Properties for Rent'}
                {selectedCity && <span className="text-blue-600"> in {selectedCity}</span>}
                {selectedCity && (
                  <button
                    onClick={clearCityFilter}
                    className="ml-3 text-sm text-slate-500 hover:text-slate-700 font-normal"
                    data-testid="clear-city-filter"
                  >
                    (Clear)
                  </button>
                )}
              </h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Property Status Filter - only when Buy tab is active */}
              {listingType === 'buy' && (
                <div className="inline-flex bg-white border border-slate-200 rounded-full p-0.5" data-testid="property-status-filter">
                  {[{ value: 'all', label: 'All' }, { value: 'off_plan', label: 'Off-plan' }, { value: 'ready', label: 'Ready' }].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => { setPropertyStatusFilter(opt.value); setCurrentPage(1); }}
                      className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${
                        propertyStatusFilter === opt.value
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                      data-testid={`status-filter-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
              {/* Rent Period Filter - only when Rent tab is active */}
              {listingType === 'rent' && (
                <div className="inline-flex bg-white border border-slate-200 rounded-full p-0.5" data-testid="rent-period-filter">
                  {[{ value: 'all', label: 'All' }, { value: 'per_day', label: 'Daily' }, { value: 'per_month', label: 'Monthly' }].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => { setRentPeriodFilter(opt.value); setCurrentPage(1); }}
                      className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${
                        rentPeriodFilter === opt.value
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                      data-testid={`rent-filter-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
              {/* Sort Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="h-10 sm:h-12 px-3 sm:px-5 rounded-full bg-white border border-slate-200 text-slate-900 font-medium hover:bg-slate-50 transition-all flex items-center gap-2"
                  data-testid="sort-button"
                >
                  <ArrowUpDown className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="text-sm sm:text-base hidden xs:inline">Sort</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                {showSortDropdown && (
                  <div className="absolute left-0 sm:left-auto sm:right-0 top-full mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-lg z-20" data-testid="sort-dropdown">
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleSort(option.value)}
                        className={`w-full px-4 py-3 text-left text-sm hover:bg-slate-50 transition-all first:rounded-t-xl last:rounded-b-xl ${sortBy === option.value ? 'bg-blue-50 text-blue-600 font-medium' : 'text-slate-700'}`}
                        data-testid={`sort-option-${option.value || 'default'}`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Filters Button */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="h-10 sm:h-12 px-3 sm:px-5 rounded-full bg-white border border-slate-200 text-slate-900 font-medium hover:bg-slate-50 transition-all flex items-center gap-2"
                data-testid="toggle-filters-button"
              >
                <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base">Filters</span>
              </button>
              {/* AI Search Button */}
              <button
                onClick={() => { setAiSearchMode(!aiSearchMode); setAiExplanation(''); }}
                className={`h-10 sm:h-12 px-3 sm:px-4 rounded-full border font-medium transition-all flex items-center gap-1.5 ${
                  aiSearchMode ? 'bg-violet-600 border-violet-600 text-white' : 'bg-white border-slate-200 text-slate-700 hover:bg-violet-50 hover:border-violet-300'
                }`}
                data-testid="ai-search-toggle"
              >
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            {aiSearchMode ? (
              <div className="relative">
                <Sparkles className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-violet-500" />
                <input
                  type="text"
                  placeholder="Try: 3BHK apartment under 50 lakhs..."
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAISearch()}
                  className="w-full h-14 rounded-full border-violet-200 bg-violet-50/50 focus:ring-2 focus:ring-violet-200 focus:border-violet-400 transition-all pl-14 pr-32"
                  data-testid="ai-search-input"
                />
                <button
                  onClick={handleAISearch}
                  disabled={aiSearching || !aiQuery.trim()}
                  className="absolute right-2 top-2 h-10 px-5 rounded-full bg-violet-600 text-white font-medium hover:bg-violet-700 transition-all disabled:opacity-50 flex items-center gap-2"
                  data-testid="ai-search-button"
                >
                  {aiSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  <span className="hidden sm:inline">{aiSearching ? 'Searching...' : 'Search'}</span>
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  name="search"
                  placeholder="Search by property title or description..."
                  value={filters.search}
                  onChange={handleFilterChange}
                  className="w-full h-14 rounded-full border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-6 pr-32"
                  data-testid="search-input"
                />
                <button
                  onClick={applyFilters}
                  className="absolute right-2 top-2 h-10 px-6 rounded-full bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all"
                  data-testid="search-button"
                >
                  Search
                </button>
              </div>
            )}
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8" data-testid="filters-panel">
              {/* Location & Property Type */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <select
                  name="city"
                  value={filters.city}
                  onChange={handleFilterChange}
                  className="h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                  data-testid="filter-city"
                >
                  <option value="">All Cities</option>
                  {availableCities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
                <input
                  type="text"
                  name="locality"
                  placeholder="Locality"
                  value={filters.locality}
                  onChange={handleFilterChange}
                  className="h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                  data-testid="filter-locality"
                />
                <select
                  name="property_type"
                  value={filters.property_type}
                  onChange={handleFilterChange}
                  className="h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                  data-testid="filter-property-type"
                >
                  <option value="">All Property Types</option>
                  <option value="apartment">Apartment</option>
                  <option value="villa">Villa</option>
                  <option value="land">Land</option>
                  <option value="commercial">Commercial</option>
                </select>
              </div>

              {/* Bedrooms */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Number of Bedrooms</label>
                <div className="flex flex-wrap gap-2">
                  {['', '1', '2', '3', '4+', '5+'].map((bed) => (
                    <button
                      key={bed}
                      type="button"
                      onClick={() => setFilters({ ...filters, bedrooms: bed })}
                      className={`h-10 px-4 rounded-lg font-medium transition-all ${
                        (filters.bedrooms === bed)
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                      data-testid={`filter-bedrooms-${bed || 'any'}`}
                    >
                      {bed === '' ? 'Any' : `${bed} BHK`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Square Feet Range */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Square Feet Range: {filters.min_sqft || '0'} - {filters.max_sqft || '5000+'} sq.ft
                </label>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <input
                      type="range"
                      name="min_sqft"
                      min="0"
                      max="5000"
                      step="100"
                      value={filters.min_sqft || 0}
                      onChange={handleFilterChange}
                      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      data-testid="filter-min-sqft-slider"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>0</span>
                      <span>Min: {filters.min_sqft || 0} sq.ft</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <input
                      type="range"
                      name="max_sqft"
                      min="0"
                      max="10000"
                      step="100"
                      value={filters.max_sqft || 10000}
                      onChange={handleFilterChange}
                      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      data-testid="filter-max-sqft-slider"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>Max: {filters.max_sqft || 10000} sq.ft</span>
                      <span>10000+</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Price</label>
                <div className="flex items-center gap-3">
                  <select
                    name="min_price"
                    value={filters.min_price}
                    onChange={handleFilterChange}
                    className="flex-1 h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                    data-testid="filter-min-price"
                  >
                    {(listingType === 'rent' ? [
                      { value: '', label: 'No min' }, { value: '5000', label: '5K' }, { value: '10000', label: '10K' },
                      { value: '15000', label: '15K' }, { value: '20000', label: '20K' }, { value: '25000', label: '25K' },
                      { value: '30000', label: '30K' }, { value: '40000', label: '40K' }, { value: '50000', label: '50K' },
                      { value: '75000', label: '75K' }, { value: '100000', label: '1 L' }, { value: '150000', label: '1.5 L' },
                      { value: '200000', label: '2 L' },
                    ] : [
                      { value: '', label: 'No min' }, { value: '500000', label: '5 L' }, { value: '1000000', label: '10 L' },
                      { value: '2000000', label: '20 L' }, { value: '3000000', label: '30 L' }, { value: '4000000', label: '40 L' },
                      { value: '5000000', label: '50 L' }, { value: '6000000', label: '60 L' }, { value: '7000000', label: '70 L' },
                      { value: '8000000', label: '80 L' }, { value: '9000000', label: '90 L' }, { value: '10000000', label: '1 Cr' },
                      { value: '15000000', label: '1.5 Cr' }, { value: '20000000', label: '2 Cr' }, { value: '30000000', label: '3 Cr' },
                      { value: '50000000', label: '5 Cr' }, { value: '100000000', label: '10 Cr' },
                    ]).map(opt => (
                      <option key={`min-${opt.value}`} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <span className="text-slate-400 font-medium text-sm">to</span>
                  <select
                    name="max_price"
                    value={filters.max_price}
                    onChange={handleFilterChange}
                    className="flex-1 h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                    data-testid="filter-max-price"
                  >
                    {(listingType === 'rent' ? [
                      { value: '', label: 'No max' }, { value: '5000', label: '5K' }, { value: '10000', label: '10K' },
                      { value: '15000', label: '15K' }, { value: '20000', label: '20K' }, { value: '25000', label: '25K' },
                      { value: '30000', label: '30K' }, { value: '40000', label: '40K' }, { value: '50000', label: '50K' },
                      { value: '75000', label: '75K' }, { value: '100000', label: '1 L' }, { value: '150000', label: '1.5 L' },
                      { value: '200000', label: '2 L' },
                    ] : [
                      { value: '', label: 'No max' }, { value: '500000', label: '5 L' }, { value: '1000000', label: '10 L' },
                      { value: '2000000', label: '20 L' }, { value: '3000000', label: '30 L' }, { value: '4000000', label: '40 L' },
                      { value: '5000000', label: '50 L' }, { value: '6000000', label: '60 L' }, { value: '7000000', label: '70 L' },
                      { value: '8000000', label: '80 L' }, { value: '9000000', label: '90 L' }, { value: '10000000', label: '1 Cr' },
                      { value: '15000000', label: '1.5 Cr' }, { value: '20000000', label: '2 Cr' }, { value: '30000000', label: '3 Cr' },
                      { value: '50000000', label: '5 Cr' }, { value: '100000000', label: '10 Cr' },
                    ]).map(opt => (
                      <option key={`max-${opt.value}`} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Verified Only Toggle */}
              <div className="mb-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Verified Properties Only</p>
                      <p className="text-sm text-slate-500">Show only properties verified by PropTick</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFilters({ ...filters, verified_only: !filters.verified_only })}
                    className={`relative w-14 h-7 rounded-full transition-all ${
                      filters.verified_only ? 'bg-blue-600' : 'bg-slate-300'
                    }`}
                    data-testid="filter-verified-toggle"
                  >
                    <span
                      className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all shadow ${
                        filters.verified_only ? 'left-8' : 'left-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Property Readiness Filter */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Property Readiness</label>
                <div className="flex flex-wrap gap-2" data-testid="property-status-filter">
                  {[
                    { value: '', label: 'All' },
                    { value: 'ready', label: 'Ready' },
                    { value: 'off_plan', label: 'Off Plan' }
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFilters({ ...filters, property_status: opt.value })}
                      className={`h-10 px-5 rounded-lg font-medium transition-all ${
                        filters.property_status === opt.value
                          ? opt.value === 'off_plan' ? 'bg-amber-500 text-white' : 'bg-blue-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                      data-testid={`filter-status-${opt.value || 'all'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={applyFilters}
                  className="h-12 px-8 rounded-full bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20"
                  data-testid="apply-filters-button"
                >
                  Apply Filters
                </button>
                <button
                  onClick={clearFilters}
                  className="h-12 px-8 rounded-full bg-white border border-slate-200 text-slate-900 font-medium hover:bg-slate-50 transition-all"
                  data-testid="clear-filters-button"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {/* Listings Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden border border-slate-100">
                  <div className="skeleton h-56 w-full"></div>
                  <div className="p-6">
                    <div className="skeleton h-4 w-24 mb-3"></div>
                    <div className="skeleton h-6 w-3/4 mb-3"></div>
                    <div className="flex gap-3 mb-3">
                      <div className="skeleton h-4 w-16"></div>
                      <div className="skeleton h-4 w-20"></div>
                    </div>
                    <div className="flex gap-2 mb-4">
                      <div className="skeleton h-5 w-16 rounded-full"></div>
                      <div className="skeleton h-5 w-14 rounded-full"></div>
                      <div className="skeleton h-5 w-12 rounded-full"></div>
                    </div>
                    <div className="skeleton h-8 w-32"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-20" data-testid="no-listings">
              <HomeIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-lg text-slate-600">No properties found</p>
              <button
                onClick={clearFilters}
                className="mt-4 h-10 px-6 rounded-full bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 mb-8 stagger-children" data-testid="listings-grid">
                {listings.map((listing) => (
                  <div
                    key={listing.id}
                    className="property-card group relative bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-blue-100 hover:shadow-xl hover:shadow-blue-900/5 transition-all duration-300 cursor-pointer"
                    onClick={() => {
                      trackEvent('listing_click', listing.id);
                      navigate(`/property/${listing.slug || listing.id}`);
                    }}
                    data-testid={`listing-card-${listing.id}`}
                  >
                    <div className="relative h-64 bg-slate-200 overflow-hidden">
                      {listing.images && listing.images.length > 0 ? (
                        <ImageCarousel images={listing.images} title={listing.title} listingId={listing.id} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <HomeIcon className="w-16 h-16 text-slate-400" />
                        </div>
                      )}
                      
                      {/* Save Heart Button */}
                      <button
                        onClick={(e) => handleSaveProperty(e, listing.id)}
                        className={`absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center transition-all z-10 ${
                          savedProperties.includes(listing.id)
                            ? 'bg-red-500 text-white shadow-lg shadow-red-500/30'
                            : 'bg-white/90 text-slate-600 hover:bg-white hover:text-red-500 shadow-lg'
                        } ${savingProperty === listing.id ? 'opacity-50 cursor-wait' : ''}`}
                        data-testid={`save-btn-${listing.id}`}
                        disabled={savingProperty === listing.id}
                      >
                        <Heart 
                          className={`w-5 h-5 ${savedProperties.includes(listing.id) ? 'fill-current' : ''}`} 
                        />
                      </button>

                      {/* Share Button */}
                      <div className="absolute top-4 right-16 z-10">
                        <button
                          onClick={(e) => { e.stopPropagation(); setShareOpenId(shareOpenId === listing.id ? null : listing.id); }}
                          className="w-10 h-10 rounded-full bg-white/90 text-slate-600 hover:bg-white hover:text-blue-600 shadow-lg flex items-center justify-center transition-all"
                          data-testid={`share-btn-${listing.id}`}
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                        {shareOpenId === listing.id && (
                          <div className="absolute right-0 top-12 bg-white rounded-xl shadow-xl border border-slate-200 py-2 w-48 z-20" data-testid={`share-menu-${listing.id}`}>
                            <button onClick={(e) => handleShareCopyLink(e, listing.slug || listing.id)} className="w-full px-4 py-2.5 text-left text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors" data-testid={`share-copy-${listing.id}`}>
                              {linkCopiedId === listing.id ? <Check className="w-4 h-4 text-green-600" /> : <Link2 className="w-4 h-4 text-slate-400" />}
                              {linkCopiedId === listing.id ? 'Link Copied!' : 'Copy Link'}
                            </button>
                            <button onClick={(e) => handleShareWhatsApp(e, listing)} className="w-full px-4 py-2.5 text-left text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors" data-testid={`share-wa-${listing.id}`}>
                              <MessageCircle className="w-4 h-4 text-green-600" />
                              Share via WhatsApp
                            </button>
                          </div>
                        )}
                      </div>

                      
                      {listing.verified && (
                        <div className="absolute bottom-4 right-4 bg-blue-600 text-white text-xs font-medium px-3 py-1 rounded-full" data-testid="verified-badge">
                          Verified
                        </div>
                      )}
                      {listing.new_launch && (
                        <div className="absolute top-4 left-4 bg-emerald-500 text-white text-xs font-semibold px-3 py-1 rounded-full" data-testid="new-launch-badge">
                          New Launch
                        </div>
                      )}
                      {listing.featured && !listing.new_launch && (
                        <div className="absolute top-4 left-4 bg-yellow-500 text-white text-xs font-medium px-3 py-1 rounded-full" data-testid="featured-badge">
                          Featured
                        </div>
                      )}
                      {listing.listing_type === 'rent' && (
                        <div className={`absolute ${listing.featured ? 'top-12' : 'top-4'} left-4 bg-green-600 text-white text-xs font-medium px-3 py-1 rounded-full`} data-testid="rent-badge">
                          For Rent
                        </div>
                      )}
                      {listing.property_status === 'off_plan' && (
                        <div className={`absolute ${listing.featured && listing.listing_type === 'rent' ? 'top-20' : (listing.featured || listing.listing_type === 'rent') ? 'top-12' : 'top-4'} left-4 bg-amber-500 text-white text-xs font-medium px-3 py-1 rounded-full`} data-testid={`off-plan-badge-${listing.id}`}>
                          Off Plan
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                        <MapPin className="w-4 h-4" />
                        <span>{listing.locality}, {listing.city}</span>
                      </div>
                      <h3 className="text-xl font-semibold mb-2 line-clamp-1">{listing.title}</h3>
                      
                      {/* Bedrooms & Size */}
                      <div className="flex items-center gap-4 mb-2 text-sm text-slate-600">
                        {listing.bedrooms && (
                          <div className="flex items-center gap-1">
                            <Bed className="w-4 h-4" />
                            <span>{listing.bedrooms} BHK</span>
                          </div>
                        )}
                        {listing.size_sqft && (
                          <div className="flex items-center gap-1">
                            <Maximize className="w-4 h-4" />
                            <span>{listing.size_sqft.toLocaleString('en-IN')} sq.ft</span>
                          </div>
                        )}
                        {!listing.size_sqft && listing.size && (
                          <div className="flex items-center gap-1">
                            <Maximize className="w-4 h-4" />
                            <span>{listing.size}</span>
                          </div>
                        )}
                      </div>

                      {/* Amenities */}
                      {listing.amenities && listing.amenities.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {listing.amenities.slice(0, 4).map((a, i) => (
                            <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full capitalize">{a.replace(/_/g, ' ')}</span>
                          ))}
                          {listing.amenities.length > 4 && (
                            <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">+{listing.amenities.length - 4}</span>
                          )}
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-2xl font-bold text-blue-600">
                            {formatPrice(listing.price)}
                            {listing.listing_type === 'rent' && <span className="text-sm font-normal text-slate-500 ml-1">{listing.rent_period === 'per_day' ? '/day' : '/month'}</span>}
                            {listing.price_onwards && <span className="text-sm font-normal text-slate-400 ml-1">onwards</span>}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-500 capitalize bg-slate-100 px-2 py-1 rounded">{listing.property_type}</p>
                          <p className="text-xs text-slate-500 capitalize mt-1">{listing.seller_type}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && (
                <div className="flex justify-center mt-8">
                  <button
                    onClick={loadMore}
                    className="h-14 px-12 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20"
                    data-testid="load-more-button"
                  >
                    Load More Properties
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrowseListings;
